/**
 * `createAppRoot` — the shared three-app bootstrap helper. AUTH-12.
 *
 * Called from each app's `main.tsx`. Closes Pitfall H2 (redirect loop on hard
 * refresh) by `await`ing `refreshOnLoad()` BEFORE `<RouterProvider>` mounts,
 * so the first render sees the real snapshot instead of UNAUTH — route
 * guards make the correct decision on the first paint.
 *
 * Seven-step sequence:
 *
 *   0. `configureAuthNetwork({ baseUrl })` — one-time module-local init so
 *      `performRefresh` + every Plan 04 action creator shares one base URL.
 *   1. (Optional) `enableMocks()` — MSW start. Awaited before any fetch.
 *   2. Splash — a cheap first paint so the user doesn't see a blank page
 *      while `refreshOnLoad` resolves (on cold start with a session cookie
 *      this can take 50–200ms for the refresh round-trip).
 *   3. `refreshOnLoad({ aud })` — CC-01 sentinel-branching bootstrap. Either
 *      commits an authenticated snapshot or leaves UNAUTH untouched. Never
 *      throws. Route loaders (requireAuth below) handle the redirect logic.
 *   4. `createTokenProvider({ onAuthFailure })` — inject force-logout
 *      handler into the api-client so unrecoverable 401s clear the store
 *      and hard-nav to /login.
 *   5. `createRouter({ context: { auth, queryClient } })` — typed router.
 *      Every route loader can read `context.auth.getSnapshot()` synchronously.
 *   6. `installDevHook(authStore)` — dev-only; no-op in prod.
 *   7. Mount the real React tree: QueryClientProvider is supplied by
 *      ApiProvider (it wraps its own); AuthProvider subscribes to the BC;
 *      RouterProvider renders the matched route.
 */
import { type ComponentType, StrictMode, type FC, type JSX } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter, type AnyRouter } from "@tanstack/react-router";
import { hashKey, type QueryClient } from "@tanstack/react-query";
import { ApiError, ApiProvider, qk } from "@linkhr/api-client";
import { AuthProvider, authStore } from "./AuthProvider.js";
import { refreshOnLoad } from "../bootstrap/refreshOnLoad.js";
import { hydrateUserFromMe } from "../bootstrap/hydrateUserFromMe.js";
import { noEmployeeMarker } from "../tokens/noEmployeeMarker.js";
import { userEmailHintStore } from "../tokens/userEmailHint.js";
import { configureAuthNetwork } from "../refresh/crossTabCoordinator.js";
import { hasHrReads } from "../refresh/authNetworkConfig.js";
import { isReconnecting, subscribeReconnecting } from "../refresh/proactiveTimer.js";
import { ReconnectNotice } from "./ReconnectNotice.js";
import type { AuthSplashProps } from "../pages/AuthSplash.js";
import { loggedOutMarker } from "../tokens/loggedOutMarker.js";
import { createTokenProvider } from "../tokens/tokenProvider.js";
import { installDevHook } from "./devHook.js";
import { UNAUTH_SNAPSHOT, type Audience, type AuthSnapshot } from "../types.js";
import { safeRedirectPath } from "../guards/redirectGuard.js";
import { currentRouterPath } from "../guards/currentRouterPath.js";
import { forceLogoutPath } from "./forceLogoutPath.js";

/** What `GET /employees/me` answers a login with no employee record. */
const NO_EMPLOYEE_RECORD = new ApiError({
  errorCode: "EMP_NOT_FOUND",
  title: "Employee not found",
  status: 404,
  correlationId: "",
});
/** The bootstrap read got no definite answer. */
const NO_ANSWER = new Error("no answer from /employees/me");

export interface CreateAppRootOptions {
  /** Audience for this app (admin / mobile / platform). */
  aud: Audience;
  /**
   * Generated routeTree from `@tanstack/router-plugin`. Type is widened to
   * `AnyRouter["routeTree"]` so this helper stays generic across all three
   * apps — consumers still get full type-safety on their own Router type
   * because the generated `./routeTree.gen.ts` augments `Register`.
   */
  routeTree: AnyRouter["routeTree"];
  /** `env.VITE_API_BASE_URL` — one base for this app's api-client + auth. */
  apiBaseUrl: string;
  /**
   * Shown during refreshOnLoad; replaced by the real router once ready.
   * `unreachable` turns on once an attempt got no answer (or a 5xx) and the
   * restore is waiting to try again; see `AuthSplashProps` for the rest.
   */
  SplashComponent: FC<AuthSplashProps>;
  /** Caller-owned QueryClient — passes through ApiProvider. */
  queryClient: QueryClient;
  /** Per-app MSW bootstrap (dynamic import). Awaited before any fetch. */
  enableMocks?: () => Promise<void>;
  /** Route to send unauth users to inside the app. @default "/" */
  defaultRoute?: string;
  /** @default "root" */
  rootElementId?: string;
  /**
   * Optional billing-warning setter wired through `<ApiProvider>` → billing
   * middleware (Phase 2 Plan 02-01 / 02-09). Apps pass
   * `useBillingWarningStore.getState().setBillingWarning` from
   * `@linkhr/design-system` here; platform aud (Link Admin) may omit.
   * Symmetric to the TokenProvider seam — keeps design-system / api-client
   * decoupled (no design-system import from @linkhr/auth).
   */
  setBillingWarning?: (severity: string | null) => void;
  /**
   * Optional path prefix for TanStack Router (D-10 path-based deploy mapping).
   * Pass `"/admin"` for HR Admin, `"/me"` for Employee Web. Omit (or leave
   * undefined) for Link Admin which lives at the root path.
   *
   * Note: This is forwarded as TanStack Router's `basepath` option (lowercase
   * 'p'). The value should NOT have a trailing slash (Router convention);
   * Vite's `base` field DOES have a trailing slash — they agree on the prefix
   * but differ in trailing-slash convention.
   */
  basePath?: string;
  /**
   * Optional component rendered by TanStack Router's `defaultNotFoundComponent`
   * when no route matches. Apps pass a design-system `<NotFoundScreen>` (which
   * pulls i18n + tokens) so an unmatched URL renders a localized 404 instead of
   * the bare "notFoundError" console warning. Kept as a seam — like
   * `SplashComponent` / `setBillingWarning` — so `@linkhr/auth` never imports
   * `@linkhr/design-system`. Omit to keep the router's default behavior.
   */
  DefaultNotFoundComponent?: FC;
  /**
   * Optional component rendered by TanStack Router's `defaultErrorComponent`
   * when a route throws. It receives the thrown value and the router's reset,
   * so the screen can show the error's correlation id and offer a retry.
   */
  DefaultErrorComponent?: ComponentType<{ error: Error; reset: () => void }>;
  /**
   * Runs once the silent session restore has settled, while the splash is
   * still up. Resolve `true` when the page is navigating away (the global
   * login forwarding a signed-in visitor into their app): the router is then
   * never mounted, so nothing flashes between the splash and the next page.
   */
  afterRestore?: (snapshot: AuthSnapshot) => Promise<boolean>;
}

/**
 * Resolves once the first real router mount has happened.
 *
 * Apps `await createAppRoot({...})` in their main.tsx so tests / Playwright
 * can await the ready signal if they need to.
 */
export async function createAppRoot(opts: CreateAppRootOptions): Promise<void> {
  // STEP 0 — one-time module-local network config. Audience selects refresh
  // path (`/auth/refresh` vs `/_platform/auth/refresh` per CC-53).
  configureAuthNetwork({ baseUrl: opts.apiBaseUrl, aud: opts.aud });

  // STEP 1 — MSW first. Any in-flight fetch under refreshOnLoad must see
  // the worker already registered or it races against real network.
  if (opts.enableMocks !== undefined) {
    await opts.enableMocks();
  }

  // STEP 2 — Splash. Cheap first paint.
  const rootElId = opts.rootElementId ?? "root";
  const rootEl = document.getElementById(rootElId);
  if (rootEl === null) {
    throw new Error(
      `createAppRoot: element #${rootElId} not found; mount point missing in index.html`,
    );
  }
  const root = createRoot(rootEl);
  const Splash = opts.SplashComponent;
  root.render(<Splash />);

  // STEP 3 — Pre-router bootstrap (AUTH-12). Never throws; falls back to
  // UNAUTH on a refusal. While it waits out a server it can't reach, for as
  // long as that takes, the splash says so instead of spinning silently.
  // The wait has no end of its own, so the splash offers one: signing in again
  // skips the restore (the explicit-logout marker) and goes to the login form.
  const signInAgain = (): void => {
    loggedOutMarker.set();
    const baseUrl = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
    window.location.assign(`${baseUrl}login`);
  };
  const { snapshot } = await refreshOnLoad({
    aud: opts.aud,
    onUnreachable: ({ failures, retryNow }) =>
      root.render(
        <Splash
          unreachable
          failures={failures}
          onRetryNow={retryNow}
          onSignInAgain={signInAgain}
        />,
      ),
    waitForServer: true,
  });
  authStore.set(snapshot);
  if (opts.afterRestore !== undefined && (await opts.afterRestore(snapshot))) return;

  // STEP 3.5 — Fire-and-forget /employees/me hydration so the topbar avatar
  // gets initials instead of "—". HR audiences only; platform admins have
  // no employee record and an accounting bearer is refused there, so the call
  // would fail by design (noise on every page load). Tolerates 404 for HR
  // admins without a linked employee row, plus network errors. Never throws.
  // Skipped for a temporary-password session: /employees/me would 403.
  if (
    hasHrReads(opts.aud) &&
    snapshot.isAuthenticated &&
    snapshot.accessToken !== null &&
    snapshot.mustChangePassword !== true
  ) {
    const lang = (document.documentElement.lang === "ar" ? "ar" : "en") as "en" | "ar";
    const lookup = hydrateUserFromMe({
      apiBaseUrl: opts.apiBaseUrl,
      accessToken: snapshot.accessToken,
      // refreshOnLoad sets snapshot.user=null on bootstrap, so prefer the
      // last-known login email persisted by performLogin.
      email: userEmailHintStore.get() ?? snapshot.user?.email ?? null,
      locale: lang,
      store: authStore,
    });
    // The shell and its screens read the same record (`useMeEmployee`): they
    // get this answer instead of asking again. With no definite answer the
    // entry is dropped, and they ask for themselves as before.
    const meKey = qk.me.employee();
    const meHash = hashKey(meKey);
    void opts.queryClient
      .prefetchQuery({
        queryKey: meKey,
        retry: false,
        queryFn: async () => {
          const found = await lookup;
          if (found.kind === "found") return found.employee;
          throw found.kind === "none" ? NO_EMPLOYEE_RECORD : NO_ANSWER;
        },
      })
      .then(() => {
        if (opts.queryClient.getQueryState(meKey)?.error === NO_ANSWER) {
          void opts.queryClient.resetQueries({ queryKey: meKey, exact: true });
        }
      });
    // Any later read that finds the record (a Retry, an invalidation) ends the
    // remembered "no record" at once.
    opts.queryClient.getQueryCache().subscribe((event) => {
      const q = event.query;
      if (q.queryHash === meHash && q.state.status === "success") noEmployeeMarker.clear();
    });
  }

  // STEP 4 — TokenProvider with force-logout on unrecoverable api-client 401.
  const tokenProvider = createTokenProvider({
    onAuthFailure: (cause: ApiError) => {
      authStore.set(UNAUTH_SNAPSHOT);
      const baseUrl = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
      window.location.assign(
        baseUrl +
          forceLogoutPath(String(cause.errorCode), opts.aud, () =>
            safeRedirectPath(currentRouterPath()),
          ),
      );
    },
    // Safety net for a token restricted after the snapshot was built: mark the
    // snapshot and go to the password change. No-op when already there, so a
    // stray 403 on that page can't loop.
    onPasswordChangeRequired: () => {
      if (currentRouterPath().split("?")[0] === "/auth/change-password") return;
      const cur = authStore.getSnapshot();
      if (cur.isAuthenticated) authStore.set({ ...cur, mustChangePassword: true });
      const baseUrl = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
      window.location.assign(`${baseUrl}auth/change-password`);
    },
  });

  // STEP 5 — Typed router. Widening the arg cast is needed because the
  // generated routeTree adds extra phantom types that don't line up with
  // `AnyRouter["routeTree"]` on every path — consumers still get full
  // safety via `Register` module augmentation in their generated file.
  const router = createRouter({
    routeTree: opts.routeTree,
    context: { auth: authStore, queryClient: opts.queryClient },
    defaultPreload: "intent",
    ...(opts.basePath !== undefined ? { basepath: opts.basePath } : {}),
    ...(opts.DefaultNotFoundComponent !== undefined
      ? { defaultNotFoundComponent: opts.DefaultNotFoundComponent }
      : {}),
    ...(opts.DefaultErrorComponent !== undefined
      ? { defaultErrorComponent: opts.DefaultErrorComponent }
      : {}),
  } as Parameters<typeof createRouter>[0]);

  // STEP 6 — Dev-only window hook for Playwright (Plan 08 specs).
  // Pass through queryClient + apiBaseUrl so the hook can drive logout /
  // populate/read tenant-scoped cache keys / post FAMILY_REVOKED on the BC.
  installDevHook({
    store: authStore,
    queryClient: opts.queryClient,
    apiBaseUrl: opts.apiBaseUrl,
    loginPath: `${(import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/"}login`,
  });

  // A session refresh that couldn't reach the server is retried in the
  // background (the session is kept). Once one succeeds, refetch whatever
  // failed meanwhile, so the screens recover without a reload.
  let wasReconnecting = isReconnecting();
  subscribeReconnecting(() => {
    const now = isReconnecting();
    if (wasReconnecting && !now) {
      void opts.queryClient.refetchQueries({
        type: "active",
        predicate: (q) => q.state.status === "error",
      });
    }
    wasReconnecting = now;
  });

  // STEP 7 — Mount. ApiProvider already wraps QueryClientProvider — we pass
  // our queryClient through so there's a single client in the tree.
  root.render(
    <StrictMode>
      <AuthProvider store={authStore} audience={opts.aud}>
        <ApiProvider
          tokenProvider={tokenProvider}
          baseUrl={opts.apiBaseUrl}
          queryClient={opts.queryClient}
          {...(opts.setBillingWarning !== undefined
            ? { setBillingWarning: opts.setBillingWarning }
            : {})}
        >
          <AppRouterBoundary router={router} />
        </ApiProvider>
        <ReconnectNotice />
      </AuthProvider>
    </StrictMode>,
  );
}

/**
 * Small boundary component so `<RouterProvider>` typing picks up the
 * generic parameter inferred from the caller's generated routeTree.
 */
function AppRouterBoundary({ router }: { router: ReturnType<typeof createRouter> }): JSX.Element {
  return <RouterProvider router={router} />;
}
